import { PlatformSettingsFormShell } from '@app/modules/social-export/components/settings/PlatformSettingsFormShell';
import { BaseSettingsFormProps } from '@app/modules/social-export/hooks/social-export-form-props';
import { socialExport } from '@mi-front/api-clients';
import { Grid } from '@mui/material';
import { useMemo } from 'react';
import { TextFieldElement } from 'react-hook-form-mui';
import { defineMessage, useIntl } from 'react-intl';
import { z } from 'zod';

const minMsg = defineMessage({
  id: 'validation.minValueZero',
  defaultMessage: 'Must be greater than or equal to 0',
});
const maxMsg = defineMessage({
  id: 'validation.maxValue60000',
  defaultMessage: 'Must be less than or equal to 60000',
});

const demoConfigTitle = defineMessage({
  id: 'socialExport.form.demoConfig',
  defaultMessage: 'Configuración de Demo',
});

const publishDelayMessage = defineMessage({
  id: 'socialExport.form.publishDelay',
  defaultMessage: 'Retraso de publicación (ms)',
});

const publishDelayHelperMessage = defineMessage({
  id: 'socialExport.form.publishDelayHelper',
  defaultMessage: 'Tiempo de espera antes de publicar (0-60000 ms, por defecto: 10000)',
});

const deleteDelayMessage = defineMessage({
  id: 'socialExport.form.deleteDelay',
  defaultMessage: 'Retraso de eliminación (ms)',
});

const deleteDelayHelperMessage = defineMessage({
  id: 'socialExport.form.deleteDelayHelper',
  defaultMessage: 'Tiempo de espera antes de eliminar (0-60000 ms, por defecto: 2000)',
});

export function getDemoSettingsSchema(t: (msg: typeof minMsg) => string) {
  return z.object({
    publishDelayMs: z.number().min(0, t(minMsg)).max(60000, t(maxMsg)).nullable().optional(),
    deleteDelayMs: z.number().min(0, t(minMsg)).max(60000, t(maxMsg)).nullable().optional(),
  });
}

/** @deprecated Use getDemoSettingsSchema(t) for translated messages */
export const demoSettingsSchema = getDemoSettingsSchema((msg) => msg.defaultMessage);

export function DemoSettingsForm({
  onUpdate,
  defaultSettings,
}: BaseSettingsFormProps<socialExport.DemoSettingsDto>) {
  const { $t } = useIntl();
  const schema = useMemo(() => getDemoSettingsSchema((msg) => $t(msg)), [$t]);
  return (
    <PlatformSettingsFormShell
      values={{
        publishDelayMs: defaultSettings?.publishDelayMs ?? 10000,
        deleteDelayMs: defaultSettings?.deleteDelayMs ?? 2000,
      }}
      schema={schema}
      sectionTitle={$t(demoConfigTitle)}
      onSuccess={async (formData) => {
        await onUpdate({
          publishDelayMs: formData.publishDelayMs ?? null,
          deleteDelayMs: formData.deleteDelayMs ?? null,
        });
      }}
    >
      <Grid container>
        <Grid item md={4}>
          <TextFieldElement
            name="publishDelayMs"
            label={$t(publishDelayMessage)}
            type="number"
            fullWidth
            inputProps={{
              min: 0,
              max: 60000,
              step: 1,
            }}
            helperText={$t(publishDelayHelperMessage)}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item md={4}>
          <TextFieldElement
            name="deleteDelayMs"
            label={$t(deleteDelayMessage)}
            type="number"
            fullWidth
            inputProps={{
              min: 0,
              max: 60000,
              step: 1,
            }}
            helperText={$t(deleteDelayHelperMessage)}
          />
        </Grid>
      </Grid>
    </PlatformSettingsFormShell>
  );
}
